import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, XCircle, RefreshCw, Zap, ShieldAlert, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { fmtVND, fmtCurrency, fmtDateTime, prettyStatus } from "../lib/format";
import { useUser } from "../context/UserContext";
import StatusChip from "../components/StatusChip";

const TRACK_STEPS = [
  { key: "pending_approval", label: "Pending approval", desc: "Waiting for admin to approve." },
  { key: "submitted", label: "Submitted to rail", desc: "Handed off to Pay10x · Thunes." },
  { key: "processing", label: "Processing", desc: "Rail is processing your payout." },
  { key: "completed", label: "Completed", desc: "Funds delivered to destination." },
];

function stepIndex(status) {
  if (status === "pending_approval") return 0;
  if (status === "approved" || status === "submitted") return 1;
  if (status === "processing") return 2;
  if (status === "completed") return 3;
  return -1;
}

export default function PayoutTracker() {
  const { id } = useParams();
  const nav = useNavigate();
  const { demoMode } = useUser();
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = await api.payout(id);
      setPayout(p);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const doAdvance = async (forceFail = false) => {
    setBusy(true);
    try {
      const p = await api.advance(id, forceFail);
      setPayout(p);
      if (p.status === "completed") toast.success("Payout completed");
      else if (p.status === "failed") toast.error("Payout failed — reversal issued");
      else toast(`Advanced to ${prettyStatus(p.status)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Advance failed");
    } finally {
      setBusy(false);
    }
  };

  const doRetry = async () => {
    setBusy(true);
    try {
      const np = await api.retry(id);
      toast.success("New payout created — awaiting approval");
      nav(`/payouts/${np.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (!payout) return <div className="text-slate-500">Payout not found</div>;

  const idx = stepIndex(payout.status);
  const isFailed = payout.status === "failed";
  const isRejected = payout.status === "rejected";

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <button
        onClick={() => nav("/payouts")}
        data-testid="tracker-back"
        className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> All payouts
      </button>

      <header className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
            Payout tracker
          </h1>
          <StatusChip status={payout.status} testId="tracker-status-chip" />
        </div>
        <p className="text-sm text-slate-500">
          {payout.rail_reference_id ? (
            <span className="inline-flex items-center gap-1.5">
              Ref
              <code className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-800">
                {payout.rail_reference_id}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(payout.rail_reference_id);
                  toast.success("Reference copied");
                }}
                className="text-slate-500 hover:text-slate-800"
                title="Copy"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : (
            "Ref will appear once submitted to the rail."
          )}
        </p>
      </header>

      {/* Amount card */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        <p className="text-xs uppercase tracking-widest text-slate-500">Net payout</p>
        <div className="mt-2 flex items-baseline gap-3 flex-wrap">
          <div className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tabular-nums">
            {fmtCurrency(payout.net_amount, payout.payout_currency)}
          </div>
          <div className="text-sm text-slate-500 font-medium">{payout.payout_currency}</div>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
          <MetaCell label="Gross" value={fmtVND(payout.amount_vnd)} />
          <MetaCell label="Fee" value={fmtVND(payout.fee_vnd)} />
          <MetaCell label="Created" value={fmtDateTime(payout.created_at)} />
        </div>
      </div>

      {/* Failure / rejection card */}
      {(isFailed || isRejected) && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-900">
                {isFailed ? "Payout failed" : "Payout rejected"}
              </p>
              <p className="text-sm text-red-700 mt-1">
                {payout.failure_reason || "The rail returned a failure. A reversal ledger entry has been created and your balance restored."}
              </p>
            </div>
          </div>
          {isFailed && (
            <button
              data-testid="payout-retry"
              disabled={busy}
              onClick={doRetry}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" /> Retry payout
            </button>
          )}
        </div>
      )}

      {/* Tracker */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-6">
          Status timeline
        </h3>
        <ol className="relative space-y-6">
          {TRACK_STEPS.map((s, i) => {
            const done = idx > i || payout.status === "completed";
            const current = idx === i && !isFailed && !isRejected;
            const failedHere = isFailed && i === 2; // fail during processing
            return (
              <li key={s.key} className="relative pl-10">
                {i < TRACK_STEPS.length - 1 && (
                  <span
                    className={`absolute left-3.5 top-7 h-full w-px ${
                      done ? "bg-teal-700" : "bg-slate-200"
                    }`}
                  />
                )}
                <span
                  className={`absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border-2 ${
                    failedHere
                      ? "bg-red-50 border-red-500 text-red-600"
                      : done
                      ? "bg-teal-700 border-teal-700 text-white"
                      : current
                      ? "bg-white border-teal-700 text-teal-700 ring-4 ring-teal-700/10"
                      : "bg-white border-slate-200 text-slate-400"
                  }`}
                >
                  {failedHere ? (
                    <XCircle className="h-3.5 w-3.5" />
                  ) : done ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : current ? (
                    <Clock className="h-3.5 w-3.5" />
                  ) : (
                    <span className="text-[10px] font-semibold">{i + 1}</span>
                  )}
                </span>
                <div>
                  <p
                    className={`font-medium ${
                      done || current ? "text-slate-900" : "text-slate-400"
                    }`}
                  >
                    {s.label}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
                </div>
              </li>
            );
          })}
        </ol>

        {/* Demo controls */}
        {demoMode &&
          !isFailed &&
          !isRejected &&
          payout.status !== "completed" && (
            <div className="mt-8 rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-orange-500" />
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  Demo controls
                </p>
              </div>
              {payout.status === "pending_approval" ? (
                <p className="text-sm text-slate-600">
                  Waiting for admin approval. Switch to Admin role in the sidebar
                  and open the Approval Queue to approve this payout.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="advance-happy"
                    disabled={busy}
                    onClick={() => doAdvance(false)}
                    className="rounded-full bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
                  >
                    Simulate next step
                  </button>
                  <button
                    data-testid="advance-fail"
                    disabled={busy}
                    onClick={() => doAdvance(true)}
                    className="rounded-full border border-red-200 bg-white px-5 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    Force failure
                  </button>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
}

const MetaCell = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className="mt-0.5 text-sm font-medium text-slate-900 tabular-nums">{value}</p>
  </div>
);
